import React from 'react';
import { Ref } from 'react-hook-form';
import { IconButton, makeStyles, Theme } from '@material-ui/core';
import { CancelRounded, InsertDriveFileOutlined } from '@material-ui/icons';
import { colorSet } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import { UploadedFileWithProperty } from './RequestFormFileInput';

export interface ReferenceFileListEntryProps extends UploadedFileWithProperty {
  onClickDeleteFile: (fileId: string) => void;
  onUpdateFile: (
    fileId: string,
    updateFileData: Partial<UploadedFileWithProperty>
  ) => void;
}

interface ReferenceFileListEntryStyleProps {
  isReference: boolean;
}

const ReferenceFileListEntry = React.forwardRef<
  Ref,
  ReferenceFileListEntryProps
>((file, ref) => {
  const { fileId, fileName, onClickDeleteFile } = file;
  const { uploadedFileEntry } = useReferenceFileListEntryStyles({
    isReference: true
  });

  return (
    <div className={uploadedFileEntry}>
      <div className="fileName">
        <div className="filename-with-icon">
          <InsertDriveFileOutlined />
          <CustomizedTypography variant="body2" color="textPrimary" noWrap>
            {fileName}
          </CustomizedTypography>
        </div>
        <IconButton
          onClick={() => onClickDeleteFile(fileId)}
          data-cy={`rfq-referenceFile-close-${fileId}`}
        >
          <CancelRounded />
        </IconButton>
      </div>
    </div>
  );
});

export default ReferenceFileListEntry;

const useReferenceFileListEntryStyles = makeStyles<
  Theme,
  ReferenceFileListEntryStyleProps
>((theme) => ({
  uploadedFileEntry: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    height: 48,
    borderRadius: 8,
    backgroundColor: colorSet.gray200,
    padding: '0px 12px',
    paddingRight: '8px',
    maxWidth: 228,
    [theme.breakpoints.down('sm')]: {
      maxWidth: () => 'unset',
      flexDirection: 'column',
      justifyContent: 'center',
      height: 48
    },
    '& .fileName': {
      width: '100%',
      position: 'relative',
      display: 'flex',
      gap: '9px',
      '& .filename-with-icon': {
        display: 'flex',
        gap: '8px',
        width: 'calc(100% - 40px)',
        alignItems: 'center'
      },
      [theme.breakpoints.down('sm')]: {
        height: '20px',
        justifyContent: 'space-between',
        alignItems: 'center',
        '& .filename-with-icon': {
          width: 'calc(100% - 64px)'
        }
      }
    },
    '& .fileProperties': {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      [theme.breakpoints.down('sm')]: {
        width: ' 100%',
        gap: '8px'
      }
    },
    '& svg': {
      width: 20,
      height: 20
    },
    '& button': {
      width: 32,
      height: 32,
      marginLeft: '2px'
    }
  },
  input: {
    maxWidth: 110,
    borderRadius: '8px',
    backgroundColor: colorSet.gray000,
    [theme.breakpoints.down('sm')]: {
      flex: 1,
      maxWidth: 'unset'
    },
    '& input': {
      borderRadius: '8px',
      padding: '10px 4px 10px 12px',
      textAlign: 'right',
      '&::placeholder': {
        color: colorSet.gray500
      }
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: colorSet.gray400
    }
  }
}));
